/**
 * FERP Portal - Instance List Page (DRR-074: Tabulator DRY Template, DRR-141: Subject Scope)
 *
 * EXECUTE Phase: Running workflow instances with tracked state and progress.
 * Delegates to TabulatorPrimitiveManager for consistent CRUD across all 25 primitives.
 *
 * Subject filter added via DRR-141 template — same useSubjectScope + SubjectFilterHeader
 * as TaskListPage. Operators can now filter instances by resident/patient/client.
 * Lifecycle phase badges show ONBOARDING/ACTIVE/EXITING/CLOSED per subject.
 *
 * DRR-141: Updating SubjectFilterHeader or useSubjectScope benefits this page automatically.
 *
 * getInstancesBySubject + getInstancesByActor resolvers wired here (DRR-141).
 * Inline gql + typed interfaces used (workflow-forms pattern) — codegen not required.
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';
import { useDevActorId } from '@/hooks/useDevActorId';
import { useResolveActorName } from '@/contexts/ActorNameContext';
import {
  useGetInstancesByActorQuery,
  useGetInstancesBySubjectQuery,
  useGetWorkflowQuery,
} from '@/generated/graphql';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import {
  useSubjectScope,
  SubjectFilterHeader,
} from '@ferp/workflow-forms';

// ---------------------------------------------------------------------------
// Inline queries — avoids need for codegen re-run
// ---------------------------------------------------------------------------

interface GqlInstanceItem {
  id: string;
  instanceName?: string | null;
  workflowName?: string | null;
  workflowDescription?: string | null;
  subjectName?: string | null;
  status: string;
  currentState: string;
  subjectActorId?: string | null;
  workflowDefinitionId?: string | null;
  [key: string]: unknown;
}

interface GetInstancesBySubjectData {
  getInstancesBySubject: GqlInstanceItem[];
}

interface GetInstancesByActorData {
  getInstancesByActor: GqlInstanceItem[];
}

export function InstanceListPage() {
  const { actorId, actorName, loading: actorLoading } = useDevActorId();
  const resolveActorName = useResolveActorName();
  const [searchParams, setSearchParams] = useSearchParams();
  const workflowFilter = searchParams.get('workflow');

  // DRR-141: Same template as TaskListPage. Update useSubjectScope to update all pages.
  const scope = useSubjectScope({ nameResolver: resolveActorName });
  const { filter, selectedSubjectId, derivedGroupBy, derivedFilterBy } = scope;

  // Workflow-filtered view uses clientFilter instead of a separate query,
  // because entity IDs (17-wf-001) differ from config IDs (wf-4-0-033) after resolution.
  // The clientFilter matches on either workflowDefinitionId or workflowName.

  // Resolve workflow name for the header
  const { data: wfNameData } = useGetWorkflowQuery({
    variables: { workflowId: workflowFilter ?? '' },
    skip: !workflowFilter,
    fetchPolicy: 'cache-first',
  });

  // "My Instances" — backend-filtered by actor (created_by)
  const {
    data: myInstancesData,
    loading: myInstancesLoading,
    error: myInstancesError,
    refetch: myInstancesRefetch,
  } = useGetInstancesByActorQuery({
    variables: { actorId: actorId ?? '' },
    skip: filter !== 'my' || !actorId,
  });

  // "Resident" tab — backend-filtered by subject actor
  const {
    data: subjectInstancesData,
    loading: subjectInstancesLoading,
    error: subjectInstancesError,
    refetch: subjectInstancesRefetch,
  } = useGetInstancesBySubjectQuery({
    variables: { subjectActorId: selectedSubjectId ?? '' },
    skip: filter !== 'subject' || !selectedSubjectId,
  });

  // Instance GraphQL type uses `instanceName` not `name` — map for PrimitiveItem compatibility.
  // Build human-readable name from resolved workflow name + subject name.
  const mapInstanceName = (items: GqlInstanceItem[] | null): PrimitiveItem[] | null => {
    if (!items) return null;
    return items.map((item) => ({
      ...item,
      name: item.instanceName || item.workflowName || `Instance ${item.id?.slice(-8) ?? ''}`,
      description: item.workflowDescription || undefined,
    })) as PrimitiveItem[];
  };

  // Client-side filter for workflow-scoped view. Matches workflowDefinitionId or workflowName
  // against the URL param (which is the workflow entity ID like "17-wf-001").
  const workflowClientFilter = useMemo(() => {
    if (!workflowFilter) return undefined;
    const wfId = workflowFilter;
    const wfName = wfNameData?.getWorkflow?.name;
    return (item: PrimitiveItem) => {
      const defId = (item as Record<string, unknown>).workflowDefinitionId as string | undefined;
      const name = (item as Record<string, unknown>).workflowName as string | undefined;
      return defId === wfId || name === wfName || defId?.includes(wfId.replace(/^\d+-/, '')) === true;
    };
  }, [workflowFilter, wfNameData]);

  const activeOverride = useMemo(() => {
    if (filter === 'my') {
      return {
        items: mapInstanceName(myInstancesData?.getInstancesByActor ?? null),
        loading: myInstancesLoading || actorLoading,
        error: myInstancesError,
        refetch: myInstancesRefetch,
      };
    }
    if (filter === 'subject' && selectedSubjectId) {
      return {
        items: mapInstanceName(subjectInstancesData?.getInstancesBySubject ?? null),
        loading: subjectInstancesLoading,
        error: subjectInstancesError,
        refetch: subjectInstancesRefetch,
      };
    }
    return undefined;
  }, [
    filter, selectedSubjectId,
    myInstancesData, myInstancesLoading, myInstancesError, myInstancesRefetch, actorLoading,
    subjectInstancesData, subjectInstancesLoading, subjectInstancesError, subjectInstancesRefetch,
  ]);

  const workflowDisplayName = wfNameData?.getWorkflow?.name;

  const headerSlot = workflowFilter ? (
    <Alert variant="light" color="violet" p="xs" mb="xs">
      <Group justify="space-between">
        <Group gap="xs">
          <Text size="sm" fw={600}>
            Instances of: {workflowDisplayName ?? workflowFilter}
          </Text>
          {wfNameData?.getWorkflow?.description && (
            <Text size="xs" c="dimmed">— {wfNameData.getWorkflow.description}</Text>
          )}
        </Group>
        <Button
          size="compact-xs"
          variant="subtle"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => setSearchParams({})}
        >
          All Instances
        </Button>
      </Group>
    </Alert>
  ) : (
    <SubjectFilterHeader
      scope={scope}
      actorName={actorName}
      actorLoading={actorLoading}
      primitiveLabel="Instances"
    />
  );

  return (
    <TabulatorPrimitiveManager
      primitiveType="instance"
      headerSlot={headerSlot}
      overrideData={activeOverride}
      clientFilter={workflowClientFilter}
      groupBy={derivedGroupBy}
      filterBy={derivedFilterBy}
    />
  );
}

export default InstanceListPage;
